import { useEffect, useMemo, useState } from 'react';
import type { KeyboardEvent } from 'react';
import {
  IconBraces,
  IconContrast2,
  IconCopy,
  IconGradienter,
  IconRefresh,
  IconSparkles,
  IconTargetArrow,
  IconLock,
  IconLockOpen,
  IconArrowLeft,
  IconArrowRight,
  IconTrash,
  IconPlus,
  IconArrowsShuffle,
  IconLayout,
  IconRulerMeasure,
} from '@tabler/icons-react';
import { NumberField, NumberFieldGroup, NumberFieldInput } from '@/components/ui/number-field';
import { getContrastRatio, getReadableTextColor } from '@/lib/accessibility';
import {
  formatHexAsOklch,
  generatePaletteCss,
  generatePaletteJson,
  HARMONY_OPTIONS,
  normalizeHex,
} from '@/lib/studio-color';
import { ColorField } from '@/components/studio/ColorField';
import { ColorInspector } from '@/components/studio/ColorInspector';
import {
  createThemeRoles,
  movePaletteColor,
  paletteSize,
  randomizePalette,
  regeneratePalette,
  resizePalette,
} from '@/lib/palette-workflow';
import type { PaletteComposerState, StudioNotify } from '@/types/studio';

interface PaletteComposerProps {
  state: PaletteComposerState;
  onChange: (state: PaletteComposerState) => void;
  onCopy: (text: string, label: string) => void;
  onTestInContrast?: (color: string) => void;
  onAddToGradient?: (color: string) => void;
  onNotify?: StudioNotify;
  onPlayground: () => void;
  onBuildScale: () => void;
  onBuildGradient: () => void;
}

export function PaletteComposer({
  state,
  onChange,
  onCopy,
  onTestInContrast,
  onAddToGradient,
  onNotify,
  onPlayground,
  onBuildScale,
  onBuildGradient,
}: PaletteComposerProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedColor = state.colors[selectedIndex] ?? state.colors[0] ?? state.seed;
  const roles = createThemeRoles(state.colors);
  const harmony = useMemo(
    () => HARMONY_OPTIONS.find((option) => option.id === state.harmony) ?? HARMONY_OPTIONS[0]!,
    [state.harmony],
  );

  useEffect(() => {
    if (selectedIndex >= state.colors.length) setSelectedIndex(0);
  }, [selectedIndex, state.colors.length]);

  const regenerate = (patch: Partial<PaletteComposerState> = {}) => {
    onChange(regeneratePalette(state, patch));
  };

  const generate = () => {
    const previous = state;
    onChange(regeneratePalette(state));
    onNotify?.('Palette generated.', { undo: () => onChange(previous) });
  };

  const toggleLock = (index: number) =>
    onChange({
      ...state,
      locked: state.locked.map((locked, i) => (i === index ? !locked : locked)),
    });
  const moveColor = (direction: number) => {
    const nextIndex = selectedIndex + direction;
    if (nextIndex < 0 || nextIndex >= state.count) return;
    onChange(movePaletteColor(state, selectedIndex, nextIndex));
    setSelectedIndex(nextIndex);
  };
  const removeColor = () => {
    if (state.count <= 2) return;
    onChange({
      ...state,
      count: paletteSize(state.count - 1),
      colors: state.colors.filter((_, i) => i !== selectedIndex),
      locked: state.locked.filter((_, i) => i !== selectedIndex),
    });
    setSelectedIndex(Math.max(0, selectedIndex - 1));
  };

  const updateSelectedColor = (color: string) => {
    const colors = [...state.colors];
    colors[selectedIndex] = normalizeHex(color);
    onChange({ ...state, colors });
  };

  const moveSelection = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let nextIndex = index;
    switch (event.key) {
      case 'ArrowRight':
        nextIndex = Math.min(index + 1, state.colors.length - 1);
        break;
      case 'ArrowLeft':
        nextIndex = Math.max(index - 1, 0);
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = state.colors.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    setSelectedIndex(nextIndex);
    const buttons = event.currentTarget
      .closest('.chromatic-spine')
      ?.querySelectorAll<HTMLButtonElement>('.chromatic-swatch');
    buttons?.[nextIndex]?.focus();
  };

  return (
    <section className="studio-tool-workspace" aria-labelledby="palette-title">
      <div className="studio-artifact-panel palette-artifact-panel">
        <header className="studio-tool-header">
          <div>
            <p className="studio-eyebrow">01 / Make something colorful</p>
            <h2 id="palette-title">Palette Composer</h2>
            <p>Find a direction. Keep what works. Make every color yours.</p>
          </div>
          <div className="studio-header-actions">
            <button
              className="studio-button"
              type="button"
              onClick={() => onChange(randomizePalette(state))}
              disabled={state.locked.every(Boolean)}
              title="Shuffle unlocked colors. Shortcut: Space outside controls."
            >
              <IconArrowsShuffle aria-hidden="true" />
              Shuffle
            </button>
            <button
              className="studio-button studio-button-primary"
              type="button"
              onClick={generate}
              disabled={state.locked.every(Boolean)}
            >
              <IconRefresh aria-hidden="true" />
              Generate
            </button>
          </div>
        </header>

        <div className="palette-stage" aria-label="Generated palette">
          <div
            className="chromatic-spine"
            style={
              {
                '--swatch-count': state.count > 6 ? Math.ceil(state.count / 2) : state.count,
              } as React.CSSProperties
            }
          >
            {state.colors.map((color, index) => {
              const active = index === selectedIndex;
              return (
                <div className="palette-swatch-cell" key={index} data-active={active || undefined}>
                  <button
                    className="chromatic-swatch"
                    type="button"
                    data-active={active || undefined}
                    style={{
                      backgroundColor: color,
                      color: getReadableTextColor(color),
                    }}
                    aria-pressed={active}
                    aria-label={`Select palette color ${index + 1}: ${color}`}
                    onClick={() => setSelectedIndex(index)}
                    onKeyDown={(event) => moveSelection(event, index)}
                  >
                    <span className="chromatic-swatch-index">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <span className="chromatic-swatch-value">{color}</span>
                  </button>
                  <button
                    type="button"
                    className="palette-lock-button"
                    style={{ color: getReadableTextColor(color) }}
                    aria-label={`${state.locked[index] ? 'Unlock' : 'Lock'} color ${index + 1}`}
                    aria-pressed={state.locked[index]}
                    onClick={() => toggleLock(index)}
                  >
                    {state.locked[index] ? <IconLock /> : <IconLockOpen />}
                  </button>
                </div>
              );
            })}
          </div>

          <div className="palette-stage-caption">
            <span>
              <IconSparkles aria-hidden="true" />
              {harmony.label}
            </span>
            <p>
              {state.count} colors · {state.locked.filter(Boolean).length} locked <kbd>Space</kbd>{' '}
              to explore
            </p>
          </div>
        </div>

        <div className="palette-workflow-actions">
          <button type="button" className="studio-button" onClick={onPlayground}>
            <IconLayout />
            Open playground
            <IconArrowRight />
          </button>
          <button type="button" className="studio-button" onClick={onBuildScale}>
            <IconRulerMeasure />
            Build scales
          </button>
          <button type="button" className="studio-button" onClick={onBuildGradient}>
            <IconGradienter />
            Create gradient
          </button>
        </div>

        <div className="palette-detail-grid">
          <article className="studio-proof-card palette-role-preview">
            <p className="studio-eyebrow">Role preview</p>
            <div
              className="role-preview-surface"
              style={{
                backgroundColor: roles.background,
                color: roles.text,
              }}
            >
              <span>In good company</span>
              <strong>
                A little color.
                <br />A lot of possibility.
              </strong>
              <span
                className="role-preview-action"
                aria-hidden="true"
                style={{
                  backgroundColor: roles.primary,
                  color: getReadableTextColor(roles.primary),
                }}
              >
                Create something
              </span>
            </div>
            <p className="studio-caption">
              Try your palette in interface, editorial, and poster layouts.
            </p>
          </article>

          <article className="studio-proof-card palette-output-card">
            <div>
              <p className="studio-eyebrow">Selected color</p>
              <strong>{selectedColor}</strong>
              <code>{formatHexAsOklch(selectedColor)}</code>
              <p className="studio-caption">
                White {getContrastRatio(selectedColor, '#FFFFFF').toFixed(2)}:1 · Black{' '}
                {getContrastRatio(selectedColor, '#000000').toFixed(2)}:1
              </p>
            </div>
            <div className="studio-inline-actions">
              <button
                type="button"
                className="studio-button"
                onClick={() => onCopy(selectedColor, 'HEX color')}
              >
                <IconCopy aria-hidden="true" />
                Copy HEX
              </button>
              <button
                type="button"
                className="studio-button"
                onClick={() => onCopy(formatHexAsOklch(selectedColor), 'OKLCH color')}
              >
                <IconCopy aria-hidden="true" />
                Copy OKLCH
              </button>
              <button
                type="button"
                className="studio-button"
                onClick={() => onCopy(generatePaletteCss(state.colors), 'CSS palette')}
              >
                <IconBraces aria-hidden="true" />
                Copy CSS
              </button>
              <button
                type="button"
                className="studio-button"
                onClick={() => onCopy(generatePaletteJson(state.colors), 'JSON palette')}
              >
                <IconBraces aria-hidden="true" />
                JSON
              </button>
              {onTestInContrast && (
                <button
                  type="button"
                  className="studio-button"
                  onClick={() => onTestInContrast(selectedColor)}
                >
                  <IconContrast2 aria-hidden="true" />
                  Test in Contrast
                </button>
              )}
              {onAddToGradient && (
                <button
                  type="button"
                  className="studio-button"
                  onClick={() => onAddToGradient(selectedColor)}
                >
                  <IconGradienter aria-hidden="true" />
                  Add to gradient
                </button>
              )}
            </div>
          </article>
        </div>
      </div>

      <aside className="studio-inspector" aria-label="Palette controls">
        <div className="studio-inspector-heading">
          <div>
            <p className="studio-eyebrow">Recipe</p>
            <h3>Palette recipe</h3>
          </div>
          <IconTargetArrow aria-hidden="true" />
        </div>

        <ColorField
          id="palette-seed"
          label="Seed color"
          value={state.seed}
          onChange={(seed) => regenerate({ seed })}
          hint="The seed anchors the harmony. Locked colors stay in place."
        />

        <div className="studio-field">
          <label htmlFor="palette-harmony" className="studio-label">
            Harmony
          </label>
          <select
            id="palette-harmony"
            className="studio-select"
            value={state.harmony}
            onChange={(event) =>
              regenerate({ harmony: event.target.value as PaletteComposerState['harmony'] })
            }
          >
            {HARMONY_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
          <span className="studio-field-hint">{harmony.shortDescription}</span>
        </div>

        <fieldset className="studio-field">
          <legend className="studio-label">Palette size</legend>
          <div className="studio-segmented">
            {[5, 6].map((count) => (
              <button
                key={count}
                type="button"
                data-active={state.count === count || undefined}
                aria-pressed={state.count === count}
                onClick={() => onChange(resizePalette(state, count))}
              >
                {count} colors
              </button>
            ))}
          </div>
          <div className="palette-count-controls">
            <button
              type="button"
              className="studio-icon-button"
              aria-label="Remove last color"
              disabled={state.count <= 2}
              onClick={() => onChange(resizePalette(state, state.count - 1))}
            >
              −
            </button>
            <span>{state.count} / 12 colors</span>
            <button
              type="button"
              className="studio-icon-button"
              aria-label="Add palette color"
              disabled={state.count >= 12}
              onClick={() => onChange(resizePalette(state, state.count + 1))}
            >
              <IconPlus />
            </button>
          </div>
        </fieldset>

        <div className="studio-range-field">
          <span>
            <span>Chroma</span>
            <NumberField
              className="w-auto flex-row items-center gap-0"
              value={Number(state.chroma.toFixed(2))}
              min={0.04}
              max={0.29}
              step={0.01}
              onValueChange={(value) => {
                if (typeof value === 'number') {
                  regenerate({ chroma: value });
                }
              }}
            >
              <NumberFieldGroup className="h-6 w-[4.25rem] border-border/60 bg-transparent">
                <NumberFieldInput
                  aria-label="Chroma"
                  className="font-mono text-[10.5px] text-foreground h-full py-0 leading-6.5 text-center"
                />
              </NumberFieldGroup>
            </NumberField>
          </span>
          <input
            id="palette-chroma"
            type="range"
            aria-label="Chroma"
            min="0.04"
            max="0.29"
            step="0.01"
            value={state.chroma}
            onChange={(event) => regenerate({ chroma: Number(event.target.value) })}
          />
        </div>

        <div className="studio-range-field">
          <span>
            <span>Lightness</span>
            <NumberField
              className="w-auto flex-row items-center gap-0"
              value={Math.round(state.lightness * 100)}
              min={35}
              max={85}
              onValueChange={(value) => {
                if (typeof value === 'number') {
                  regenerate({ lightness: value / 100 });
                }
              }}
            >
              <NumberFieldGroup className="h-6 w-[4.25rem] border-border/60 bg-transparent">
                <NumberFieldInput
                  aria-label="Lightness in percent"
                  className="font-mono text-[10.5px] text-foreground h-full py-0 leading-6.5 text-center"
                />
              </NumberFieldGroup>
            </NumberField>
          </span>
          <input
            id="palette-lightness"
            type="range"
            aria-label="Lightness"
            min="0.35"
            max="0.85"
            step="0.01"
            value={state.lightness}
            onChange={(event) => regenerate({ lightness: Number(event.target.value) })}
          />
        </div>

        <div className="studio-inspector-divider" />

        <div className="studio-selection-title">
          <span>Selected color</span>
          <div className="studio-inline-actions">
            <button
              type="button"
              className="studio-icon-button"
              aria-label="Move color left"
              disabled={selectedIndex === 0}
              onClick={() => moveColor(-1)}
            >
              <IconArrowLeft />
            </button>
            <button
              type="button"
              className="studio-icon-button"
              aria-label="Move color right"
              disabled={selectedIndex >= state.count - 1}
              onClick={() => moveColor(1)}
            >
              <IconArrowRight />
            </button>
            <button
              type="button"
              className="studio-icon-button"
              aria-label="Remove selected color"
              disabled={state.count <= 2}
              onClick={removeColor}
            >
              <IconTrash />
            </button>
          </div>
        </div>
        <ColorInspector
          color={selectedColor}
          index={selectedIndex}
          onChange={updateSelectedColor}
          onCopy={onCopy}
        />

        <button
          className="studio-button studio-button-wide"
          type="button"
          onClick={() => regenerate({ seed: selectedColor })}
        >
          <IconTargetArrow aria-hidden="true" />
          Use selected as seed
        </button>

        <div className="studio-boundary-note">
          <strong>Room to experiment</strong>
          <p>
            Lock colors before generating. Undo and redo work across the studio. Check final text
            pairs in Contrast.
          </p>
        </div>
      </aside>
    </section>
  );
}
